import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { EVENT_CATEGORIES } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Trash2, Plus } from "lucide-react";
import FileField, { StoredImg } from "@/components/stock/FileField";

const KIND_LABEL = { opening: "Ouverture", closing: "Clôture" };

export default function PageLibrary() {
  const [pages, setPages] = useState([]);
  const [draft, setDraft] = useState({ name: "", kind: "opening", category: "all", image: "" });

  const load = () => api.get("/doc-pages").then(({ data }) => setPages(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!draft.name) return toast.error("Nom requis");
    if (!draft.image) return toast.error("Image requise");
    try {
      await api.post("/doc-pages", { name: draft.name, kind: draft.kind, category: draft.category === "all" ? null : draft.category, image: draft.image });
    } catch (e) { return toast.error(apiError(e.response?.data?.detail) || "Enregistrement impossible"); }
    toast.success("Page enregistrée"); setDraft({ name: "", kind: "opening", category: "all", image: "" }); load();
  };
  const del = async (id) => { await api.delete(`/doc-pages/${id}`); load(); };

  return (
    <div className="mb-4 rounded-xl border border-zinc-800 bg-zinc-900 p-3" data-testid="page-library">
      <Label className="text-zinc-400">Bibliothèque de pages</Label>
      <p className="mt-0.5 text-xs text-zinc-500">Enregistrez plusieurs pages d'ouverture/clôture réutilisables, taguées par type d'événement. Elles sont proposées automatiquement dans le devis selon la catégorie.</p>

      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
        <Input data-testid="lib-name" placeholder="Nom (ex : Ouverture mariage)" className="border-zinc-800 bg-zinc-950" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
        <Select value={draft.kind} onValueChange={(v) => setDraft({ ...draft, kind: v })}>
          <SelectTrigger data-testid="lib-kind" className="border-zinc-800 bg-zinc-950"><SelectValue /></SelectTrigger>
          <SelectContent className="border-zinc-800 bg-zinc-900">
            <SelectItem value="opening">Ouverture</SelectItem>
            <SelectItem value="closing">Clôture</SelectItem>
          </SelectContent>
        </Select>
        <Select value={draft.category} onValueChange={(v) => setDraft({ ...draft, category: v })}>
          <SelectTrigger data-testid="lib-category" className="border-zinc-800 bg-zinc-950"><SelectValue /></SelectTrigger>
          <SelectContent className="border-zinc-800 bg-zinc-900">
            <SelectItem value="all">Toutes catégories</SelectItem>
            {EVENT_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="mt-2">
        <FileField testid="lib-image" kind="page" accept="image/png,image/jpeg,image/webp" value={draft.image} onChange={(v) => setDraft((d) => ({ ...d, image: v }))} />
      </div>
      <div className="mt-2 flex items-center gap-3">
        <Button data-testid="lib-add" onClick={add} className="rounded-full bg-[#FF6A00] font-semibold text-white hover:bg-[#E65F00]"><Plus className="mr-1 h-4 w-4" />Enregistrer la page</Button>
      </div>

      {pages.length > 0 && (
        <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3">
          {pages.map((p) => (
            <div key={p.id} data-testid={`lib-page-${p.id}`} className="flex items-center gap-2 rounded-lg border border-zinc-800 bg-zinc-950 p-2">
              <StoredImg value={p.image} className="h-12 w-10 rounded border border-zinc-800 object-cover" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm text-white">{p.name}</p>
                <p className="truncate text-[11px] text-zinc-500">{KIND_LABEL[p.kind] || p.kind}{p.category ? ` · ${p.category}` : " · Toutes"}</p>
              </div>
              <button data-testid={`lib-del-${p.id}`} onClick={() => del(p.id)} className="text-zinc-600 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
